/**
 * The Network board — Norican's WAN, as Cato's SASE fabric sees it.
 *
 * Everything here is read from Cato's GraphQL API through the NOVA backend,
 * which caches one estate-wide snapshot per minute. Nothing is seeded and
 * nothing is simulated: when Cato is not configured the board says so, names
 * exactly what is missing, and renders no sites at all.
 */

import { Icon, Waypoints } from "@norican/icons";
import { Alert, Freshness, PageHeader, Skeleton } from "@norican/react";

import { NAV_SECTIONS } from "@/components/nav-sections";
import { useNetworkLinks, useNetworkOverview } from "@/lib/api/hooks/network";
import { logger } from "@/lib/logger";

import { BandwidthPanel } from "./bandwidth-panel";
import { EventFeed } from "./event-feed";
import { LinkMatrix } from "./link-matrix";
import { LinkQuality } from "./link-quality";
import { NotConfigured } from "./not-configured";
import { SaseSummary } from "./sase-summary";
import { SiteTable } from "./site-table";
import { SocketFleet } from "./socket-fleet";

const SECTION = NAV_SECTIONS.find((section) => section.label === "Network");

export function NetworkPage() {
  const { data, isPending, error } = useNetworkOverview();
  const configured = data?.configured ?? false;
  // No point asking for links while Cato is unconfigured — the answer is a
  // known-empty payload the overview already carries.
  const links = useNetworkLinks(configured);

  const summary = data?.summary;
  // The backend refreshes on its own interval; give the dot a little slack past
  // it before it escalates on its own.
  const staleAfterMs = summary ? summary.poll_interval_sec * 1000 * 1.5 : undefined;

  if (error) {
    logger.warn("[network] overview request failed", error);
  }

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <PageHeader
        title="Network"
        subtitle={SECTION?.description}
        actions={
          summary?.fetched_at && configured ? (
            <Freshness label="Cato" since={summary.fetched_at} staleAfter={staleAfterMs} />
          ) : undefined
        }
      />

      {error && (
        <Alert tone="danger" title="Couldn't load the network status">
          {error instanceof Error ? error.message : "The NOVA network API did not respond."}
        </Alert>
      )}

      {summary?.last_error && configured && (
        <Alert
          tone="warning"
          icon={<Icon icon={Waypoints} size="sm" />}
          title="Part of this board is missing data"
        >
          {summary.last_error}
        </Alert>
      )}

      {isPending && !data ? (
        <div className="flex flex-col gap-4" aria-busy="true">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((index) => (
              <Skeleton key={index} height="7rem" />
            ))}
          </div>
          <Skeleton height="20rem" />
        </div>
      ) : (
        data &&
        summary &&
        (configured ? (
          <>
            <SaseSummary summary={summary} />
            <SiteTable sites={data.sites ?? []} />
            <LinkMatrix links={links.data?.links ?? []} isPending={links.isPending} />
            <SocketFleet sites={data.sites ?? []} summary={summary} />
            <BandwidthPanel
              sites={data.sites ?? []}
              windowSeconds={summary.metrics_window_seconds}
            />
            <LinkQuality sites={data.sites ?? []} windowSeconds={summary.metrics_window_seconds} />
            <EventFeed
              events={data.events ?? []}
              retentionDays={summary.event_retention_days ?? 3}
            />
          </>
        ) : (
          <NotConfigured overview={data} />
        ))
      )}
    </main>
  );
}
